import type { Playbook } from "@workspace/api-client-react";
import { ExternalLink, Headphones, Youtube } from "lucide-react";
import { FaInstagram, FaLinkedinIn, FaSpotify, FaTiktok } from "react-icons/fa";
import { FaThreads } from "react-icons/fa6";
import { SiApplepodcasts, SiMedium, SiSubstack } from "react-icons/si";
import { cn } from "@/lib/utils";

function extractYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1]! : null;
}

function sourceDomain(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function PlatformFallback({
  sourceType,
  className,
}: {
  sourceType?: string | null;
  className?: string;
}) {
  const box = cn(
    "w-11 h-11 rounded-md flex items-center justify-center shrink-0 bg-white/70 border border-black/5 shadow-sm",
    className,
  );

  switch (sourceType) {
    case "instagram":
      return (
        <div className={box}>
          <FaInstagram className="w-5 h-5 text-pink-600" />
        </div>
      );
    case "linkedin":
      return (
        <div className={box}>
          <FaLinkedinIn className="w-5 h-5 text-sky-700" />
        </div>
      );
    case "tiktok":
      return (
        <div className={box}>
          <FaTiktok className="w-5 h-5 text-foreground" />
        </div>
      );
    case "twitter":
      return (
        <div className={cn(box, "text-sm font-black")}>𝕏</div>
      );
    case "threads":
      return (
        <div className={box}>
          <FaThreads className="w-5 h-5 text-foreground" />
        </div>
      );
    case "spotify":
      return (
        <div className={cn(box, "bg-emerald-50")}>
          <FaSpotify className="w-5 h-5 text-emerald-600" />
        </div>
      );
    case "apple_podcast":
      return (
        <div className={cn(box, "bg-violet-50")}>
          <SiApplepodcasts className="w-5 h-5 text-violet-600" />
        </div>
      );
    case "substack":
      return (
        <div className={box}>
          <SiSubstack className="w-5 h-5 text-orange-600" />
        </div>
      );
    case "medium":
      return (
        <div className={box}>
          <SiMedium className="w-5 h-5 text-foreground" />
        </div>
      );
    case "article":
    case "ted":
    default:
      return (
        <div className={cn(box, "bg-sky-50")}>
          {sourceType === "ted" ? (
            <span className="text-[10px] font-black text-red-600">TED</span>
          ) : isPodcastType(sourceType) ? (
            <Headphones className="w-5 h-5 text-primary" />
          ) : (
            <ExternalLink className="w-5 h-5 text-primary" />
          )}
        </div>
      );
  }
}

function isPodcastType(sourceType?: string | null): boolean {
  return sourceType === "spotify" || sourceType === "apple_podcast";
}

function isVideoType(sourceType?: string | null): boolean {
  return sourceType === "youtube" || sourceType === "ted" || !sourceType;
}

const SOCIAL_SOURCE_TYPES = new Set(["instagram", "linkedin", "tiktok", "twitter", "threads"]);

function isSocialType(sourceType?: string | null): boolean {
  return !!sourceType && SOCIAL_SOURCE_TYPES.has(sourceType);
}

/** First "What you'll do" step in the preview sheet — phrased for whatever this
 * trigger actually is, not always "the video". Unset sourceType still reads as
 * video, matching the same assumption PlaybookThumbnail's play-overlay makes. */
export function playbookRecallStep(playbook: Pick<Playbook, "sourceType">): string {
  if (isPodcastType(playbook.sourceType)) return "Listen to or recall the episode";
  if (isSocialType(playbook.sourceType)) return "Watch or recall the post";
  if (isVideoType(playbook.sourceType)) return "Watch or recall the video";
  return "Read or recall the article";
}

export function PlaybookThumbnail({
  playbook,
  size = "md",
  showPlayOverlay = true,
}: {
  playbook: Pick<Playbook, "videoUrl" | "icon" | "sourceType"> & { thumbnailUrl?: string | null };
  size?: "sm" | "md" | "lg";
  showPlayOverlay?: boolean;
}) {
  const dims =
    size === "lg" ? "w-20 h-14 rounded-xl" : size === "sm" ? "w-9 h-9 rounded-md" : "w-11 h-11 rounded-md";

  const youtubeId = playbook.videoUrl ? extractYouTubeId(playbook.videoUrl) : null;
  const thumbnailUrl =
    playbook.thumbnailUrl ||
    (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg` : null);

  if (thumbnailUrl) {
    return (
      <div className={cn("relative overflow-hidden shrink-0 bg-black/10 shadow-sm", dims)}>
        <img src={thumbnailUrl} alt="" className="w-full h-full object-cover" />
        {showPlayOverlay && isVideoType(playbook.sourceType) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-black/40 flex items-center justify-center">
              {playbook.sourceType === "youtube" ? (
                <div className="w-0 h-0 border-y-[3px] border-y-transparent border-l-[6px] border-l-white ml-0.5" />
              ) : (
                <div className="w-0 h-0 border-y-[3px] border-y-transparent border-l-[6px] border-l-white ml-0.5" />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!playbook.sourceType && youtubeId) {
    return (
      <div className={cn("relative overflow-hidden shrink-0 bg-black/10 shadow-sm", dims)}>
        <img
          src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
          alt=""
          className="w-full h-full object-cover"
        />
        {showPlayOverlay && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-black/40 flex items-center justify-center">
              <div className="w-0 h-0 border-y-[3px] border-y-transparent border-l-[6px] border-l-white ml-0.5" />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (playbook.icon && !playbook.sourceType && !youtubeId) {
    return <span className="text-2xl leading-none">{playbook.icon}</span>;
  }

  return <PlatformFallback sourceType={playbook.sourceType} className={dims} />;
}

export function playbookSourceLabel(playbook: Pick<Playbook, "sourceType" | "videoUrl">): string {
  if (playbook.sourceType === "youtube" || extractYouTubeId(playbook.videoUrl ?? "")) {
    return "Watch on YouTube";
  }
  if (isPodcastType(playbook.sourceType)) {
    return "Listen to source";
  }
  const domain = sourceDomain(playbook.videoUrl);
  if (domain) return domain;
  return "View source";
}

export function PlaybookSourceIcon({ sourceType, videoUrl }: { sourceType?: string; videoUrl?: string }) {
  if (sourceType === "youtube" || extractYouTubeId(videoUrl ?? "")) {
    return <Youtube className="w-3 h-3 text-red-400 shrink-0" />;
  }
  if (isPodcastType(sourceType)) {
    return <Headphones className="w-3 h-3 shrink-0" />;
  }
  return <ExternalLink className="w-3 h-3 shrink-0" />;
}
